import { useCallback, useEffect, useMemo, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import DefaultLayout from '../../../layouts/DefaultLayout';
import { Helmet } from 'react-helmet-async';
import apiClient from '../../../services/api/apiClient';
import { IClient } from '../../../utils/types/IClient';
import ClientSelect from '../../../components/select/ClientSelect';
import ReleveClientGrid from '../../../components/grid/ReleveClientGrid';
import { ReleveClientDTO } from '../../../utils/types/objects/ReleveClientDTO';
import { NumericFormat } from 'react-number-format';
import { SoldeClientDTO } from '../../../utils/types/objects/SoldeClientDTO';
import { useQuery } from '@tanstack/react-query';
import QueryErrorAlerts, { QueryErrorEntry } from '../../../components/others/QueryErrorAlerts';

function ReleveClientReport() {
    const [selectedCardCode, setSelectedCardCode] = useState<string | null>(null);

    // ---

    const {
        data: clients = [],
        isLoading: isLoadingClients,
        isFetching: isFetchingClients,
        refetch: refetchClients,
        isError: isClientsError,
        error: clientsError,
    } = useQuery<IClient[]>({
        queryKey: ['clients'],
        queryFn: async () => {
            const res = await apiClient.get('/api/clients/getClients');
            return res.data ?? [];
        },
        meta: { title: 'Chargement des clients' },
        staleTime: 1000 * 60 * 10, // 10 min
    });

    const {
        data: synthese,
        isError: isSyntheseError,
        error: syntheseError,
        refetch: loadSynthese,
    } = useQuery<SoldeClientDTO | null>({
        queryKey: [
            'syntheseClient',
            {
                codeClient: selectedCardCode,
            },
        ],
        queryFn: async () => {
            if (!selectedCardCode) return null;

            const response = await apiClient.get<SoldeClientDTO | null>('/api/clients/get-solde-client', {
                params: {
                    codeClient: selectedCardCode,
                },
            });

            return response.data ?? null;
        },
        meta: { title: 'Chargement de la synthèse client' },
        enabled: false,
        staleTime: 0,
    });

    const {
        data: reportItems = [],
        isLoading: isReportItemsLoading,
        isError: isReportItemsError,
        error: reportItemsError,
        refetch: loadReportItems,
    } = useQuery<ReleveClientDTO[]>({
        queryKey: [
            'releveItems',
            {
                codeClient: selectedCardCode,
            },
        ],
        queryFn: async () => {
            if (!selectedCardCode) return [];

            const response = await apiClient.get<ReleveClientDTO[]>('/api/clients/get-releve-client', {
                params: {
                    codeClient: selectedCardCode,
                },
            });

            return response.data ?? [];
        },
        meta: { title: 'Chargement du relevé client' },
        enabled: false,
        staleTime: 0,
    });

    // ---

    const handleLoadSyntheseClient = useCallback(async () => {
        if (!selectedCardCode) return;
        loadSynthese();
    }, [selectedCardCode, loadSynthese]);

    const handleLoadRelevesItems = useCallback(async () => {
        if (!selectedCardCode) return;
        loadReportItems();
    }, [selectedCardCode, loadReportItems]);

    const loadData = useCallback(async () => {
        if (!selectedCardCode) return;

        (async () => {
            await handleLoadSyntheseClient();
            await handleLoadRelevesItems();
        })();
    }, [selectedCardCode, handleLoadSyntheseClient, handleLoadRelevesItems]);

    // ---

    useEffect(() => {
        if (clients != null && clients.length === 1) {
            setSelectedCardCode(clients[0].cardCode);
        }
    }, [clients]);

    useEffect(() => {
        if (selectedCardCode && clients.length === 1) {
            loadData();
        }
    }, [clients, selectedCardCode, loadData]);

    // ---

    const queryErrors = useMemo<QueryErrorEntry[]>(
        () => [
            { isError: isClientsError, error: clientsError, title: 'Erreur lors du chargement des clients', refetch: refetchClients },
            { isError: isSyntheseError, error: syntheseError, title: 'Erreur lors du chargement de la synthèse client', refetch: handleLoadSyntheseClient },
            { isError: isReportItemsError, error: reportItemsError, title: 'Erreur lors du chargement du relevé client', refetch: handleLoadRelevesItems },
        ],
        [isClientsError, clientsError, refetchClients, isSyntheseError, syntheseError, handleLoadSyntheseClient, isReportItemsError, reportItemsError, handleLoadRelevesItems],
    );

    // ---

    return (
        <>
            <Helmet>
                <title>Relevé client :: Portail PETROSIL</title>
            </Helmet>

            <div>
                <div className="page-header d-print-none">
                    <div className="container-xl">
                        <div className="row g-2 align-items-center">
                            <div className="col-12 col-md-auto ms-auto d-print-none order-1 order-md-1">
                                <form
                                    className="row gy-2 gx-3 align-items-center"
                                    onSubmit={(e) => {
                                        e.preventDefault();

                                        if (!selectedCardCode) return;
                                        loadData();
                                    }}
                                >
                                    <div className="col-auto">
                                        <div className="input-group">
                                            <span className="input-group-text">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon icon-tabler icons-tabler-outline icon-tabler-users-group">
                                                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                                    <path d="M10 13a2 2 0 1 0 4 0a2 2 0 0 0 -4 0" />
                                                    <path d="M8 21v-1a2 2 0 0 1 2 -2h4a2 2 0 0 1 2 2v1" />
                                                    <path d="M15 5a2 2 0 1 0 4 0a2 2 0 0 0 -4 0" />
                                                    <path d="M17 10h2a2 2 0 0 1 2 2v1" />
                                                    <path d="M5 5a2 2 0 1 0 4 0a2 2 0 0 0 -4 0" />
                                                    <path d="M3 13v-1a2 2 0 0 1 2 -2h2" />
                                                </svg>
                                            </span>

                                            <ClientSelect clients={clients} isClientsLoading={isLoadingClients || isFetchingClients} selectedCardCode={selectedCardCode} setSelectedCardCode={setSelectedCardCode} />
                                        </div>
                                    </div>

                                    <div className="col-auto">
                                        <button
                                            className="btn btn-6 w-100 btn-icon"
                                            aria-label="Actualiser"
                                            onClick={() => {
                                                if (!selectedCardCode) return;
                                                loadData();
                                            }}
                                            disabled={isReportItemsLoading}
                                            type="button"
                                        >
                                            {isReportItemsLoading ? (
                                                <>
                                                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                                                </>
                                            ) : (
                                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="icon icon-tabler icons-tabler-outline icon-tabler-refresh">
                                                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                                    <path d="M20 11a8.1 8.1 0 0 0 -15.5 -2m-.5 -4v4h4" />
                                                    <path d="M4 13a8.1 8.1 0 0 0 15.5 2m.5 4v-4h-4" />
                                                </svg>
                                            )}
                                        </button>
                                    </div>
                                </form>
                            </div>

                            <div className="col order-0 order-md-0">
                                <div className="page-pretitle">Rapport</div>
                                <h2 className="page-title">Relevé client</h2>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="page-body">
                    <div className="container-xl">
                        <QueryErrorAlerts queries={queryErrors} />

                        {synthese && (
                            <div className="card mb-2">
                                <div className="card-header">
                                    <button className="btn btn-link p-0 w-100 text-start text-decoration-none" data-bs-toggle="collapse" data-bs-target="#syntheseCollapse" aria-expanded="true" aria-controls="syntheseCollapse">
                                        <div className="d-flex justify-content-between align-items-center">
                                            <h5 className="mb-0 text-dark">
                                                <i className="fas fa-chart-pie me-2"></i>
                                                Synthèse
                                            </h5>
                                        </div>
                                    </button>
                                </div>

                                <div id="syntheseCollapse" className="collapse show">
                                    <div className="card-body py-2">
                                        <div className="row g-2">
                                            {/* Colonne Paiements */}
                                            <div className="col-md-4">
                                                <div className="d-flex justify-content-between border-bottom pb-1">
                                                    <small className="text-muted">Paiement Station</small>
                                                    <NumericFormat value={synthese.paiementPOS} displayType="text" thousandSeparator=" " decimalSeparator="," decimalScale={2} suffix="$" className="fw-semibold text-end small" />
                                                </div>
                                                <div className="d-flex justify-content-between border-bottom pb-1">
                                                    <small className="text-muted">Paiement Banque</small>
                                                    <NumericFormat value={synthese.paiementSAP} displayType="text" thousandSeparator=" " decimalSeparator="," decimalScale={2} suffix="$" className="fw-semibold text-end small" />
                                                </div>
                                                <div className="d-flex justify-content-between">
                                                    <small className="text-muted">Total Paiement</small>
                                                    <NumericFormat value={synthese.totalPaiement} displayType="text" thousandSeparator=" " decimalSeparator="," decimalScale={2} suffix="$" className="fw-semibold text-end small" />
                                                </div>
                                            </div>

                                            {/* Colonne Livraisons */}
                                            <div className="col-md-4">
                                                <div className="d-flex justify-content-between border-bottom pb-1">
                                                    <small className="text-muted">Solde ouverture</small>
                                                    <NumericFormat value={synthese.soldeOuverture} displayType="text" thousandSeparator=" " decimalSeparator="," decimalScale={2} suffix="$" className="fw-semibold text-end small" />
                                                </div>
                                                <div className="d-flex justify-content-between border-bottom pb-1">
                                                    <small className="text-muted">Livraison Sans Carte</small>
                                                    <NumericFormat value={synthese.livraisonsSansCarte} displayType="text" thousandSeparator=" " decimalSeparator="," decimalScale={2} suffix="$" className="fw-semibold text-end small" />
                                                </div>
                                                <div className="d-flex justify-content-between">
                                                    <small className="text-muted">Recharge Cartes</small>
                                                    <NumericFormat value={synthese.recharge} displayType="text" thousandSeparator=" " decimalSeparator="," decimalScale={2} suffix="$" className="fw-semibold text-end small" />
                                                </div>
                                            </div>

                                            {/* Colonne Solde */}
                                            <div className="col-md-4">
                                                <div className="bg-light rounded p-2 h-100 text-center">
                                                    <small className="text-muted d-block">Solde Final</small>
                                                    <NumericFormat value={synthese.solde} displayType="text" thousandSeparator=" " decimalSeparator="," decimalScale={2} suffix="$" className="fs-5 fw-bold text-primary" />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        <ReleveClientGrid items={reportItems} />
                    </div>
                </div>
            </div>
        </>
    );
}

ReleveClientReport.Layout = DefaultLayout;

export default ReleveClientReport;
